(() => {
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const mobileMedia = window.matchMedia('(max-width: 900px)');
  const LOOP_IMAGE = 'https://cdn.shopify.com/s/files/1/1080/6447/3374/files/vr-botanicals-loop.webp?v=1784066985';
  const CLEAN_SESAME = 'https://cdn.shopify.com/s/files/1/1080/6447/3374/files/vr-ingredient-sesame-clean.jpg?v=1784066563';
  const CLEAN_FENUGREEK = 'https://cdn.shopify.com/s/files/1/1080/6447/3374/files/vr-ingredient-fenugreek-clean.jpg?v=1784066580';

  const loadWhyLoopWhenNeeded = (image) => {
    if (reduceMotion.matches || navigator.connection?.saveData) return;

    let requested = false;
    const requestLoop = () => {
      if (requested) return;
      requested = true;

      const preload = new Image();
      preload.decoding = 'async';
      preload.src = LOOP_IMAGE;

      const swap = async () => {
        try {
          await preload.decode();
        } catch (_) {
          // The load event still confirms the asset is available.
        }
        if (!image.isConnected) return;
        image.removeAttribute('srcset');
        image.src = LOOP_IMAGE;
        image.classList.add('vrx-why__loop');
        image.dataset.vrxLoopState = 'ready';
      };

      if (preload.complete) swap();
      else preload.addEventListener('load', swap, { once: true });
    };

    if (!('IntersectionObserver' in window)) {
      requestLoop();
      return;
    }

    const target = image.closest('.vrx-why') || image;
    const observer = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;
      observer.disconnect();
      requestLoop();
    }, { rootMargin: '240px 0px' });

    observer.observe(target);
  };

  const replaceApprovedMedia = (root) => {
    const whyImage = root.querySelector('.vrx-why__image img');
    const whyVideoConfigured = Boolean(
      document.querySelector('.vr-live-patch')?.dataset.vrxWhyVideo
    );
    if (whyImage && !whyVideoConfigured && whyImage.dataset.vrxLoopManaged !== 'true') {
      whyImage.dataset.vrxLoopManaged = 'true';
      whyImage.alt = 'Vana Ritual bottle emerging among botanical ingredients';
      whyImage.decoding = 'async';

      if (mobileMedia.matches) {
        whyImage.loading = 'lazy';
        whyImage.fetchPriority = 'low';
        loadWhyLoopWhenNeeded(whyImage);
      } else if (whyImage.src !== LOOP_IMAGE) {
        whyImage.src = LOOP_IMAGE;
        whyImage.removeAttribute('srcset');
        whyImage.classList.add('vrx-why__loop');
        whyImage.loading = 'eager';
      }
    }

    const sesame = root.querySelector('.vrx-ingredient img[alt="Sesame"]');
    if (sesame) sesame.src = CLEAN_SESAME;

    const fenugreek = root.querySelector('.vrx-ingredient img[alt="Fenugreek"]');
    if (fenugreek) fenugreek.src = CLEAN_FENUGREEK;
  };

  const init = (root) => {
    if (!root || root.dataset.vrxReady === 'true') return;
    root.dataset.vrxReady = 'true';

    replaceApprovedMedia(root);

    const reveal = root.querySelectorAll('[data-vrx-reveal]');
    const progress = root.querySelector('[data-vrx-progress]');
    const progressTrack = progress?.parentElement;
    const siteHeader = document.querySelector('.header-section');
    const pageScroller = document.querySelector('.page-wrapper');
    const hero = root.querySelector('[data-vrx-hero]');
    const product = root.querySelector('[data-vrx-product]');
    const sticky = root.querySelector('[data-vrx-sticky]');
    const track = root.querySelector('[data-vrx-track]');
    const previous = root.querySelector('[data-vrx-prev]');
    const next = root.querySelector('[data-vrx-next]');

    if ('IntersectionObserver' in window && !reduceMotion.matches) {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      }, { threshold: 0.12, rootMargin: '0px 0px -7% 0px' });
      reveal.forEach((item) => observer.observe(item));
    } else {
      reveal.forEach((item) => item.classList.add('is-visible'));
    }

    root.querySelectorAll('[data-vrx-accordion-group]').forEach((group) => {
      group.querySelectorAll('details').forEach((detail) => {
        detail.addEventListener('toggle', () => {
          if (!detail.open) return;
          group.querySelectorAll('details[open]').forEach((other) => {
            if (other !== detail) other.open = false;
          });
        });
      });
    });

    root.querySelectorAll('a[href^="#"]').forEach((link) => {
      link.addEventListener('click', (event) => {
        const selector = link.getAttribute('href');
        if (!selector || selector === '#') return;
        const target = document.querySelector(selector);
        if (!target) return;
        event.preventDefault();
        target.scrollIntoView({ behavior: reduceMotion.matches ? 'auto' : 'smooth', block: 'start' });
      });
    });

    if (track) {
      const cards = Array.from(track.querySelectorAll('.vrx-ritual-card'));
      const ritualSection = track.closest('.vrx-ritual');
      let ritualIndex = 0;
      let ritualTimer = null;
      let resumeTimer = null;
      let scrollSettleTimer = null;
      let ritualHasEntered = false;

      const normalizeIndex = (index) => {
        if (!cards.length) return 0;
        return (index + cards.length) % cards.length;
      };

      const goToRitual = (index, behavior = 'smooth') => {
        if (!cards.length) return;
        ritualIndex = normalizeIndex(index);
        const card = cards[ritualIndex];
        track.scrollTo({
          left: Math.max(0, card.offsetLeft - track.offsetLeft),
          behavior: reduceMotion.matches ? 'auto' : behavior
        });
      };

      const stopAutoplay = () => {
        if (ritualTimer) window.clearInterval(ritualTimer);
        ritualTimer = null;
      };

      const startAutoplay = () => {
        stopAutoplay();
        if (reduceMotion.matches || cards.length < 2 || document.hidden) return;
        ritualTimer = window.setInterval(() => goToRitual(ritualIndex + 1), 1700);
      };

      const pauseThenResume = () => {
        stopAutoplay();
        if (resumeTimer) window.clearTimeout(resumeTimer);
        resumeTimer = window.setTimeout(startAutoplay, 2600);
      };

      const resetToFirstRitual = () => {
        ritualIndex = 0;
        track.scrollTo({ left: 0, behavior: 'auto' });
      };

      resetToFirstRitual();
      window.requestAnimationFrame(resetToFirstRitual);

      previous?.addEventListener('click', () => {
        goToRitual(ritualIndex - 1);
        pauseThenResume();
      });

      next?.addEventListener('click', () => {
        goToRitual(ritualIndex + 1);
        pauseThenResume();
      });

      track.addEventListener('pointerdown', pauseThenResume, { passive: true });
      track.addEventListener('touchstart', pauseThenResume, { passive: true });
      track.addEventListener('focusin', stopAutoplay);
      track.addEventListener('focusout', pauseThenResume);
      track.addEventListener(
        'scroll',
        () => {
          if (scrollSettleTimer) window.clearTimeout(scrollSettleTimer);
          scrollSettleTimer = window.setTimeout(() => {
            const trackLeft = track.getBoundingClientRect().left;
            let closestIndex = 0;
            let closestDistance = Number.POSITIVE_INFINITY;
            cards.forEach((card, index) => {
              const distance = Math.abs(card.getBoundingClientRect().left - trackLeft);
              if (distance < closestDistance) {
                closestDistance = distance;
                closestIndex = index;
              }
            });
            ritualIndex = closestIndex;
          }, 100);
        },
        { passive: true }
      );
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) stopAutoplay();
        else if (ritualHasEntered) startAutoplay();
      });
      window.addEventListener('pageshow', resetToFirstRitual);

      if ('IntersectionObserver' in window && ritualSection) {
        const ritualObserver = new IntersectionObserver(
          (entries) => {
            const entry = entries[0];
            if (!entry?.isIntersecting) {
              stopAutoplay();
              return;
            }
            if (!ritualHasEntered) {
              ritualHasEntered = true;
              resetToFirstRitual();
              window.requestAnimationFrame(() => {
                resetToFirstRitual();
                startAutoplay();
              });
              return;
            }
            startAutoplay();
          },
          { threshold: 0.18 }
        );
        ritualObserver.observe(ritualSection);
      } else {
        ritualHasEntered = true;
        resetToFirstRitual();
        startAutoplay();
      }
    }

    let ticking = false;
    const update = () => {
      const usesPageScroller = pageScroller && pageScroller.scrollHeight > pageScroller.clientHeight;
      const scrollTop = usesPageScroller ? pageScroller.scrollTop : window.scrollY;
      const total = Math.max(
        1,
        usesPageScroller
          ? pageScroller.scrollHeight - pageScroller.clientHeight
          : document.documentElement.scrollHeight - window.innerHeight
      );
      if (progressTrack) {
        const headerBottom = siteHeader ? Math.max(0, Math.round(siteHeader.getBoundingClientRect().bottom)) : 0;
        progressTrack.style.setProperty('--vrx-progress-top', `${headerBottom}px`);
      }
      if (progress) progress.style.transform = `scaleX(${Math.min(1, Math.max(0, scrollTop / total))})`;

      if (sticky && hero && product) {
        const heroRect = hero.getBoundingClientRect();
        const productRect = product.getBoundingClientRect();
        const policies = root.querySelector('.vrx-policies');
        const policyRect = policies?.getBoundingClientRect();
        const passedHero = heroRect.bottom < window.innerHeight * 0.25;
        const productVisible = productRect.top < window.innerHeight * 0.86 && productRect.bottom > window.innerHeight * 0.18;
        const nearPolicies = policyRect ? policyRect.top < window.innerHeight * 0.75 : false;
        sticky.classList.toggle('is-visible', passedHero && !productVisible && !nearPolicies);
      }
      ticking = false;
    };

    const requestUpdate = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(update);
    };

    update();
    window.addEventListener('scroll', requestUpdate, { passive: true });
    pageScroller?.addEventListener('scroll', requestUpdate, { passive: true });
    window.addEventListener('resize', requestUpdate, { passive: true });

  };

  const initAll = (scope = document) => scope.querySelectorAll('[data-vrx]').forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initAll());
  else initAll();
  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
